/**
 * TransactionFormModal (#1315 subtask 05 pt2) — the +Neu / edit / move form,
 * plus the #2678 ``categorize`` act.
 *
 * A controlled modal that edits one planned transaction. Validation +
 * payload-building live in the pure ``transactionForm`` helper; this file is
 * just the form shell + local field state. On a valid submit it hands a
 * ``PlannedTransactionInput`` to ``onSubmit`` (the page wires that to
 * ``useUpsertPlannedTransaction`` so the curve + verdict recompute instantly).
 * "Move" is just changing the date — ``period_ym`` is re-derived.
 *
 * THREE acts, one component (#2678):
 *  - ``create``     — a brand-new plan (+Neu). Everything editable.
 *  - ``edit``       — an existing plan. Everything editable; a date change moves it.
 *  - ``categorize`` — a bank line the FARMER is only classifying. The booking already
 *    happened, so its facts (Betrag · Datum · Konto · Buchungstext) render as a
 *    read-only context block instead of as inputs: the sign, the amount, the account
 *    and the date cannot be nudged into contradicting the actual they group with
 *    (``liq-plan-<txid>``, #1494). Menge/Preis stay reachable behind a disclosure so
 *    the #1612 t × €/t prefill survives without inviting an accidental Plan↔Ist delta.
 *    ``create``/``edit`` markup is UNCHANGED by this mode — pinned by
 *    ``TransactionFormModal.parity.test.tsx``.
 */
import { useMemo, useState } from 'react';
import {
  buildPlannedInput,
  categoriesForPicker,
  computeTotal,
  suggestedCategories,
  type BuildResult,
  type DateBounds,
  type TransactionFormState,
} from './transactionForm';
import { categoryLabel } from './ledgerCategory';
import { accountLabel, type LiquidityAccount } from './accounts';
import CounterpartyPicker from './CounterpartyPicker';
import { formatEur } from '../../../utils/formatEur';
import type { PlannedTransactionInput } from './optimisticPlanned';
import type { PlannedTransaction } from './types';
import './TransactionFormModal.css';

const formatDeAmount = (eur: number): string =>
  eur.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDeQty = (q: number): string =>
  q.toLocaleString('de-DE', { maximumFractionDigits: 3 });

/** ``2025-11-12`` → ``12.11.2025`` — the same day format the ledger renders (#2678). */
const formatDay = (iso: string): string => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

function initialState(initial?: PlannedTransaction, defaultAccountId?: string): TransactionFormState {
  // Pre-fill volume × price. An amount-only line (no qty) reads as 1 × total,
  // so editing it shows the total as the unit price with qty 1.
  const unitPrice = initial?.unit_price_eur ?? initial?.amount_eur;
  return {
    derived_fact_id: initial?.derived_fact_id,
    ledger_category: initial?.ledger_category ?? 'unsorted',
    direction: initial?.direction ?? 'expense',
    label: initial?.label ?? '',
    qty: initial?.qty != null ? formatDeQty(initial.qty) : '1',
    unit_price: unitPrice != null ? formatDeAmount(unitPrice) : '',
    value_date: initial?.value_date ?? '',
    notes: initial?.notes ?? '',
    qty_meta: initial?.qty_meta, // carried through untouched (no UI field)
    // #2211: the edited row's own account wins (reassign starts at the current
    // Konto), else the page default (selected Konto, else overlay-primary). Left
    // undefined for single-account tenants (no default passed) so the wire key
    // stays absent — see ``buildPlannedInput``.
    account_id: initial?.account_id ?? defaultAccountId,
    // #2678 c: seed the Gegenpartei from the edited/booked row so an edit that never
    // opens the picker still RE-SENDS the link. The upsert overwrites fact_value
    // wholesale, so a dropped key here is a silent un-link. Unlike account_id there
    // is no default to fall back to — an unlinked row stays unlinked.
    counterparty_id: initial?.counterparty_id,
  };
}

export interface TransactionFormModalProps {
  /** Which act this modal is performing (#2678). ``categorize`` is a booked bank
   *  line being classified — see the file header. It carries its OWN date policy
   *  (the booked date is context, never an input), so the old ``lockDate`` prop is
   *  gone: the mode IS the state, rather than two proxies standing in for it. */
  mode: 'create' | 'edit' | 'categorize';
  initial?: PlannedTransaction;
  /** Valid plan-date window; submits outside it are rejected (phantom-row guard). */
  bounds?: DateBounds;
  /** The 9-domain id of an already-classified document tied to this transaction
   *  (a matched bank line, a contracted ``doc_ref`` invoice/Gutschrift). When set,
   *  its crosswalk shortlist is floated to the top of the category picker and
   *  surfaced as a "Vorschlag" hint. Absent → no prior, full direction-filtered
   *  vocab, nothing pre-suggested (§4.6). */
  priorDomain?: string;
  /** The tenant's real bank accounts (#2211). Passed ONLY by multi-account tenants
   *  — its presence is what renders the Konto select. Single-account tenants pass
   *  nothing, so the modal markup stays byte-identical and no account_id is sent. */
  accounts?: LiquidityAccount[];
  /** The Konto preselected for a create (the selected Konto, else overlay-primary).
   *  On edit the row's own account wins over this. Only meaningful with ``accounts``. */
  defaultAccountId?: string;
  /** This act's record GROUPS onto an existing transaction (#3152/#3172): the
   *  grouping create over a payed-less commitment, or an edit of a plan whose
   *  transaction also holds a contracted/payed sibling. Narrows the create-mode
   *  affordances that grouped semantics contradict — the Konto moves to
   *  read-only context when the commitment carries one (#2256 treatment), and
   *  the Einnahme/Ausgabe toggle is not offered (the sign is the commitment's).
   *  The Datum control deliberately STAYS (#3152 — a still-open expectation may
   *  legitimately be re-dated). Set by the page where the act starts
   *  (``handleCategorize``'s payed-less branch, ``editPlanned``); the modal
   *  never re-infers it from ``initial.derived_fact_id`` (#2678). Absent ⇒
   *  markup is byte-identical (parity gate). */
  grouping?: boolean;
  onSubmit: (input: PlannedTransactionInput) => void;
  onClose: () => void;
  submitting?: boolean;
  /** A FAILED save's user-facing message (#3152) — derived by the page from the
   *  mutation error (``planSaveErrorMessage``). Rendered as a dismissible
   *  ``role="alert"`` line in the client-validation-alert slot; the modal stays
   *  open (the page closes it only on success — unchanged). Cleared locally on
   *  the next submit or any field change; the prop transitioning to a NEW error
   *  re-shows it. */
  serverError?: string;
}

export default function TransactionFormModal({
  mode,
  initial,
  bounds,
  priorDomain,
  accounts,
  defaultAccountId,
  grouping = false,
  onSubmit,
  onClose,
  submitting = false,
  serverError,
}: TransactionFormModalProps) {
  const [state, setState] = useState<TransactionFormState>(() => initialState(initial, defaultAccountId));
  // #2678: the booked-bank-line act. Everything that follows branches on THIS, not on
  // a separate flag the caller has to keep in sync with the mode.
  const isCategorize = mode === 'categorize';
  // Render the Konto select only when the page supplies real accounts (multi-account
  // tenants). ``accountsFromEntities`` already filters out revenue_bucket / the
  // __tenant_total__ sentinel, so every entry here is a selectable Konto. In
  // ``categorize`` the Konto is a booked fact, so it moves to the context block
  // instead — a save can then never re-home the plan off the booking's account (#2256).
  // The one exception: a booking that carries NO account has no booked Konto to show,
  // so the select stays (exactly as before #2678) rather than leaving the farmer with
  // neither a statement of where the plan lands nor a way to say.
  // #3172 extends the same treatment to ``grouping``: re-homing the plan off the
  // commitment's account would split the grouped pair across scope partitions
  // (kategorisieren re-offered under one scope, an orphan plan under the other) and
  // a second save on the shared deterministic id would last-write-wins overwrite
  // the first — so the Konto renders as read-only context instead (below), with the
  // identical account-less escape. ONE mode predicate for "the Konto is a pinned
  // fact, not an input", shared by the select-withdrawal here and the context
  // rendering below, so a future pinned mode cannot widen one without the other.
  const accountIsPinned = isCategorize || grouping;
  const showAccounts =
    !!accounts && accounts.length > 0 && (!accountIsPinned || initial?.account_id == null);
  const [errors, setErrors] = useState<BuildResult['errors']>({});
  // #3152 — local visibility for the ``serverError`` prop. Hidden by a dismiss,
  // any field change, or the next submit; re-armed whenever the prop CHANGES
  // (the mutation reset drops it to undefined before a new failure sets it
  // again, so even an identical message transitions and re-shows). The
  // compare-previous-prop-in-render pattern is React's documented way to adjust
  // state on a prop change without an effect.
  const [serverErrorHidden, setServerErrorHidden] = useState(false);
  const [prevServerError, setPrevServerError] = useState(serverError);
  if (prevServerError !== serverError) {
    setPrevServerError(serverError);
    setServerErrorHidden(false);
  }
  // Whether the ``categorize`` Menge/Preis disclosure is unfolded. Real state, not a
  // derivation — see ``withMengengeruest``. Starts folded: the point of the disclosure
  // is that the booked money is not an open invitation to edit.
  const [mengengeruestOpen, setMengengeruestOpen] = useState(false);
  // The booked date, captured from the prefill. In ``categorize`` the Datum is NOT an
  // input at all (it is context), so the grouped record's date cannot desync from its
  // payed sibling's cashflow date (#1616 — that guarantee used to rest on a read-only
  // input plus a submit-side re-pin; the re-pin below stays as the belt to the braces).
  const lockedDate = isCategorize ? (initial?.value_date ?? '') : undefined;
  // The bank's own text for the booking, frozen at open: the Bezeichnung input starts
  // from the same string but the farmer may rename it, and the context block must keep
  // saying what the BANK said.
  const bookedText = initial?.label ?? '';
  // Signed by the booked direction so an Ausgabe reads negative, as everywhere else.
  const bookedSignedEur =
    initial != null ? (initial.direction === 'expense' ? -initial.amount_eur : initial.amount_eur) : 0;
  // The account this act PINS the plan to — in ``categorize`` the account the
  // BOOKING carries (#2256), under ``grouping`` the commitment's (#3172). Resolved
  // off ``initial.account_id`` and NOT off ``state.account_id``, which falls back to
  // ``defaultAccountId`` (the page's selected/overlay-primary Konto): under a
  // read-only context caption that fallback would assert a Konto the record never
  // carried; account-less facts are a designed-for state (``bankToRecord`` emits
  // ``account_id`` conditionally, and ``accountPartition`` exists to route records
  // whose account matches none). Named only when the tenant passed accounts to name
  // it from — no label source (a single-account tenant, or an account outside
  // ``accounts``, the partition case) omits the context rather than showing a raw
  // id; the seeded ``state.account_id`` still rides the payload either way.
  const pinnedAccount = initial?.account_id
    ? accounts?.find((a) => a.id === initial.account_id)
    : undefined;

  // The grouped 2-level options + the crosswalk suggestion shortlist react to the
  // chosen sign (and any document prior). Recomputed only when those change. The
  // currently-selected category is force-included so an edit of a row whose stored
  // category the filter would drop (accrual-only / sign-mismatched, seeded
  // out-of-band) still renders a matching option rather than silently mis-showing.
  const pickerGroups = useMemo(
    () => categoriesForPicker(state.direction, priorDomain, state.ledger_category),
    [state.direction, priorDomain, state.ledger_category],
  );
  const suggestions = useMemo(
    () => suggestedCategories(state.direction, priorDomain),
    [state.direction, priorDomain],
  );

  // Each act names itself. "Neue Transaktion" over a booking the bank already made
  // was the #2678 headline complaint — nothing is new there.
  const heading =
    mode === 'edit'
      ? 'Transaktion bearbeiten'
      : isCategorize
        ? 'Buchung kategorisieren'
        : 'Neue Transaktion';
  // …and so does its primary action: you are assigning a category to a booking, not
  // saving a new plan.
  const submitLabel = isCategorize ? 'Zuordnen' : 'Speichern';
  const set = (patch: Partial<TransactionFormState>) => {
    // Any field change clears a stale server error (#3152) — the farmer is
    // already composing the next attempt.
    setServerErrorHidden(true);
    setState((s) => ({ ...s, ...patch }));
  };

  // Flipping the sign (L0 filter) may strand the selected category on the wrong
  // side. Keep it if it's still offered for the new sign, otherwise fall back to
  // the not-yet-categorised bucket so the picker never shows an out-of-range value.
  const setDirection = (direction: 'income' | 'expense') => {
    setServerErrorHidden(true); // a field change, same as ``set`` (#3152)
    setState((s) => {
      const stillValid = categoriesForPicker(direction, priorDomain).some((g) =>
        g.options.some((o) => o.id === s.ledger_category),
      );
      return { ...s, direction, ledger_category: stillValid ? s.ledger_category : 'unsorted' };
    });
  };

  /** Wrap the money fields in the ``categorize`` disclosure, or hand them back
   *  untouched for ``create``/``edit`` — a bare fragment adds no DOM node, which is
   *  what keeps those two modes byte-identical (parity gate, #2678).
   *
   *  STRICTLY controlled — ``open`` from state, and the summary's own click
   *  ``preventDefault``ed so the native toggle never runs behind React's back. Two
   *  traps this avoids:
   *   - ``open={Boolean(errors.qty || errors.unit_price)}`` alone is NOT controlled:
   *     React writes ``open`` only when the PROP changes and never reconciles it against
   *     a user toggle, so after a manual fold a second failed submit would leave the
   *     panel shut with the reason inside it (a dead Zuordnen — the exact bug the raise
   *     below exists to prevent), while clearing one error with another still standing
   *     would yank the panel shut around the input being typed in.
   *   - pairing it with ``onToggle`` instead is browser-correct but relies on the
   *     ``toggle`` EVENT, which is queued as a task (async), so React state lags the DOM
   *     for any synchronous caller — including every test in this file.
   *  Raised explicitly from ``handleSubmit`` on a money error; never auto-closes. */
  const withMengengeruest = (fields: React.ReactNode): React.ReactNode =>
    isCategorize ? (
      <details className="liq-txform__mengengeruest" open={mengengeruestOpen}>
        <summary
          onClick={(e) => {
            e.preventDefault(); // state is the single source of truth for ``open``
            setMengengeruestOpen((open) => !open);
          }}
        >
          Mengengerüst erfassen
        </summary>
        {fields}
      </details>
    ) : (
      fields
    );

  // #3172 — the ONE close guard: no close path is live while a save is in flight
  // (all modes). Closing unmounts the modal, so a later 422/502 would report
  // nowhere (the #3152 serverError alert only helps while mounted, and closeForm's
  // upsert.reset() clears isError) — the optimistic row would just vanish on
  // rollback with no explanation. Every close path (backdrop, Escape, Abbrechen)
  // goes through THIS, so the rule cannot drift per-path; Abbrechen additionally
  // reads ``disabled`` for the visible affordance.
  const requestClose = () => {
    if (!submitting) onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // A stale server message never outlives the attempt it reports (#3152): this
    // submit yields its own outcome — client errors below, or a fresh mutation
    // whose failure transitions the prop and re-shows the line.
    setServerErrorHidden(true);
    // In ``categorize``, re-pin ``value_date`` to the booked date before building the
    // input — ``buildPlannedInput`` re-derives ``period_ym`` from it — so the saved plan
    // can never desync from its grouped payed sibling (#1616). #2678 removed the Datum
    // control from this mode, which closes the desync by construction; this stays as the
    // authority on the date, so re-introducing any date control here cannot reopen it.
    const submitState =
      lockedDate != null && lockedDate !== '' ? { ...state, value_date: lockedDate } : state;
    const result = buildPlannedInput(submitState, bounds);
    if (!result.ok || !result.input) {
      setErrors(result.errors);
      // Unfold the disclosure when the rejection is about the money it hides — a 0 €
      // booking prefills a Preis ``parseDeAmount`` rejects (≤ 0), and a Zuordnen that
      // fails with its reason folded away is indistinguishable from a dead button.
      if (result.errors.qty || result.errors.unit_price) setMengengeruestOpen(true);
      return;
    }
    onSubmit(result.input);
  };

  return (
    <div
      className="liq-txform__backdrop"
      onMouseDown={requestClose}
      role="presentation"
    >
      <div
        className="liq-txform"
        role="dialog"
        aria-modal="true"
        aria-label={heading}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') requestClose();
        }}
      >
        <h2 className="liq-txform__title">{heading}</h2>
        <form className="liq-txform__form" onSubmit={handleSubmit}>
          {/* #2678 — what the bank actually said, as context rather than as inputs.
              Betrag/Datum/Konto have no control anywhere in this mode, so none of them
              can be nudged into contradicting the actual this plan groups with. */}
          {isCategorize && (
            <div className="liq-txform__booked">
              <span className="liq-txform__booked-caption">Gebuchte Daten</span>
              <dl className="liq-txform__booked-list">
                <div className="liq-txform__booked-row">
                  <dt>Betrag</dt>
                  <dd>{formatEur(bookedSignedEur)}</dd>
                </div>
                <div className="liq-txform__booked-row">
                  <dt>Datum</dt>
                  <dd>{formatDay(lockedDate ?? '')}</dd>
                </div>
                {pinnedAccount && (
                  <div className="liq-txform__booked-row">
                    <dt>Konto</dt>
                    <dd>{accountLabel(pinnedAccount)}</dd>
                  </div>
                )}
                <div className="liq-txform__booked-row">
                  <dt>Buchungstext</dt>
                  {/* ``trim()`` before the dash fallback: a whitespace-only bank text is
                      truthy and would render a visually empty row. */}
                  <dd>{bookedText.trim() || '—'}</dd>
                </div>
              </dl>
              {/* The Datum's error slot moves here with the date itself — otherwise an
                  out-of-window booked date would reject the submit with nothing on
                  screen to explain it, and Zuordnen would read as a dead button. */}
              {errors.value_date && (
                <span className="liq-txform__error" role="alert">{errors.value_date}</span>
              )}
            </div>
          )}

          {/* L0 — the in/out sign hard-filters the category vocab below it. Not offered
              in ``categorize``: the sign is already determined by the booked amount, and
              flipping it would save a plan that contradicts its own actual (#2678). Not
              offered under ``grouping`` either (#3172): the sign is the commitment's, and
              a flipped plan under the same transaction_id would contradict it — headRecord
              (planned outranks contracted) would then headline the row with the
              contradiction. ``state.direction`` keeps its seed, so the payload and the
              category vocab keep the commitment's sign. */}
          {!isCategorize && !grouping && (
            <div className="liq-txform__direction" role="group" aria-label="Art">
              <button
                type="button"
                aria-pressed={state.direction === 'income'}
                className={state.direction === 'income' ? 'is-active' : ''}
                onClick={() => setDirection('income')}
              >
                Einnahme
              </button>
              <button
                type="button"
                aria-pressed={state.direction === 'expense'}
                className={state.direction === 'expense' ? 'is-active' : ''}
                onClick={() => setDirection('expense')}
              >
                Ausgabe
              </button>
            </div>
          )}

          {/* L1 group → L2 category. The stored value is the L2 ledger_category id.
              Suggested (crosswalk) options are floated to the top of their group. */}
          <label className="liq-txform__field">
            <span>Kategorie</span>
            <select
              aria-label="Kategorie"
              value={state.ledger_category}
              onChange={(e) => set({ ledger_category: e.target.value as TransactionFormState['ledger_category'] })}
            >
              {pickerGroups.map((g) => (
                <optgroup key={g.id} label={g.label}>
                  {g.options.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.suggested ? `★ ${o.label}` : o.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            {suggestions.length > 0 && (
              <span className="liq-txform__hint">
                Vorschlag aus Dokument-Klasse: {suggestions.map((id) => categoryLabel(id)).join(' · ')}
              </span>
            )}
          </label>

          {/* Konto — the owning bank account (#2211). Only multi-account tenants
              pass ``accounts``; single-account markup is unchanged. Always present +
              preselected when shown, so an explicit account is always persisted. */}
          {showAccounts && (
            <label className="liq-txform__field">
              <span>Konto</span>
              <select
                aria-label="Konto"
                value={state.account_id ?? ''}
                onChange={(e) => set({ account_id: e.target.value })}
              >
                {accounts!.map((a) => (
                  <option key={a.id} value={a.id}>
                    {accountLabel(a)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* #3172 — the grouping act's pinned Konto, as read-only context in the
              select's place. ``grouping`` has no "Gebuchte Daten" block (nothing is
              booked — the sibling is a still-open commitment), so the pin gets its
              own field-shaped row. Rendered only when there is an account to name
              (``pinnedAccount`` needs both ``initial.account_id`` and a label
              source), mirroring categorize's omission rules — an account outside
              ``accounts`` (the partition case) renders neither select nor context;
              the seeded ``state.account_id`` still rides the payload untouched
              either way (wire pin — the backend is extra="forbid" and omission =
              delete). */}
          {!isCategorize && grouping && pinnedAccount && (
            <div className="liq-txform__field">
              <span>Konto</span>
              <span className="liq-txform__grouped-konto-value">{accountLabel(pinnedAccount)}</span>
            </div>
          )}

          <label className="liq-txform__field">
            <span>Bezeichnung</span>
            <input
              aria-label="Bezeichnung"
              type="text"
              value={state.label}
              onChange={(e) => set({ label: e.target.value })}
            />
            {errors.label && <span className="liq-txform__error" role="alert">{errors.label}</span>}
          </label>

          {/* Gegenpartei — WHO the transaction is with, as an ``organization`` link
              (#2678 c). Offered in ALL THREE acts on purpose: it is a fact of the
              transaction, not of the act, and a categorize-only picker would strand
              the link the moment the row grows a plan and re-opens through the pencil
              (``edit``) with no way to change or remove it. Offering it symmetrically
              also keeps ``create`` and ``edit`` rendering identically to each other —
              #2678-a's acceptance criterion, pinned by the parity snapshots.

              Distinct from the read-only „Buchungstext" above, which stays the RAW
              bank string: that value is ``counterparty || purpose || 'Buchung'``, a
              fallback chain, so on any row without a counterparty it is purpose text
              and naming it „Gegenpartei" would mis-state it — the same class of
              mis-identification #2741 flags one level down. Identity lives here. */}
          <CounterpartyPicker
            value={state.counterparty_id}
            onChange={(counterparty_id) => set({ counterparty_id })}
          />

          {/* Menge × Preis and their live total. In ``categorize`` they sit behind a
              disclosure (below) — still prefilled, so an untouched Zuordnen preserves the
              #1612 t × €/t breakdown, but no longer an open invitation to manufacture a
              Plan↔Ist delta on a row that had none (#2678). ``create``/``edit`` render
              them inline exactly as before: a fragment adds no DOM. */}
          {withMengengeruest(
            <>
              <div className="liq-txform__row">
                <label className="liq-txform__field liq-txform__field--qty">
                  <span>Menge</span>
                  <input
                    aria-label="Menge"
                    type="text"
                    inputMode="decimal"
                    placeholder="z. B. 1"
                    value={state.qty}
                    onChange={(e) => set({ qty: e.target.value })}
                  />
                  {errors.qty && <span className="liq-txform__error" role="alert">{errors.qty}</span>}
                </label>

                <label className="liq-txform__field">
                  <span>Preis je Einheit (€)</span>
                  <input
                    aria-label="Preis je Einheit (€)"
                    type="text"
                    inputMode="decimal"
                    placeholder="z. B. 6.000,00"
                    value={state.unit_price}
                    onChange={(e) => set({ unit_price: e.target.value })}
                  />
                  {errors.unit_price && (
                    <span className="liq-txform__error" role="alert">{errors.unit_price}</span>
                  )}
                </label>
              </div>

              <div className="liq-txform__total" aria-live="polite">
                <span>Gesamtbetrag</span>
                <strong>
                  {computeTotal(state.qty, state.unit_price) != null
                    ? formatEur(computeTotal(state.qty, state.unit_price)!)
                    : '—'}
                </strong>
              </div>
            </>,
          )}

          {/* The Datum is an input only where the farmer legitimately picks one. In
              ``categorize`` the booked date is a fact, rendered in the context block
              above — see ``handleSubmit``'s re-pin (#1616, #2678). */}
          {!isCategorize && (
            <label className="liq-txform__field">
              <span>Datum</span>
              <input
                aria-label="Datum"
                type="date"
                min={bounds?.minExclusive}
                max={bounds?.max}
                value={state.value_date}
                onChange={(e) => set({ value_date: e.target.value })}
              />
              {errors.value_date && <span className="liq-txform__error" role="alert">{errors.value_date}</span>}
            </label>
          )}

          <label className="liq-txform__field">
            <span>Notiz</span>
            <textarea
              aria-label="Notiz"
              rows={2}
              placeholder="Optional: Hintergrund, Annahmen, Abweichungen …"
              value={state.notes}
              onChange={(e) => set({ notes: e.target.value })}
            />
          </label>

          {/* #3152 — a FAILED save's message, in the same alert slot pattern the
              client-validation errors use. It concerns the whole submit (not one
              field), so it sits with the actions. Absent prop ⇒ no DOM (parity). */}
          {serverError && !serverErrorHidden && (
            <div className="liq-txform__server-error" role="alert">
              <span className="liq-txform__error">{serverError}</span>
              <button
                type="button"
                className="liq-txform__server-error-dismiss"
                aria-label="Fehlermeldung schließen"
                onClick={() => setServerErrorHidden(true)}
              >
                ×
              </button>
            </div>
          )}

          <div className="liq-txform__actions">
            <button type="button" className="liq-txform__cancel" onClick={requestClose} disabled={submitting}>
              Abbrechen
            </button>
            <button type="submit" className="liq-txform__save" disabled={submitting}>
              {submitting ? `${submitLabel} …` : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
